import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

const DAYS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

/**
 * DarkDatePicker — Premium date picker for the design system.
 *
 * IMPORTANT: Renders calendar INLINE using position:absolute so that:
 * 1. Radix Dialog's DismissableLayer treats clicks as "inside" the React tree
 * 2. The calendar is NOT clipped by parent overflow:auto (since transform
 *    on Radix Dialog creates new containing block, position:fixed won't escape it)
 */
const DarkDatePicker = ({ value, onChange, placeholder = 'Selecionar data...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value) {
      const parts = value.split('-');
      if (parts.length === 3) {
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, 1);
      }
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  });
  const containerRef = useRef(null);
  const calendarRef = useRef(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const formatYMD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const formatDisplay = (ymd) => {
    if (!ymd) return '';
    const parts = ymd.split('-');
    if (parts.length !== 3) return ymd;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const handleToggle = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(prev => !prev);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    // Small delay to avoid the toggle click being caught
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    }, 10);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen]);

  const handleSelect = useCallback((date) => {
    onChange(formatYMD(date));
    setIsOpen(false);
  }, [onChange]);

  const handleShortcut = useCallback((date) => {
    onChange(formatYMD(date));
    setViewDate(new Date(date.getFullYear(), date.getMonth(), 1));
    setIsOpen(false);
  }, [onChange]);

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const prevMonth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };
  const nextMonth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const isSelected = (day) => {
    if (!day || !value) return false;
    const sel = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return sel === value;
  };

  const isToday = (day) => {
    if (!day) return false;
    return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  // Scroll calendar into view when opened
  useEffect(() => {
    if (isOpen && calendarRef.current) {
      setTimeout(() => {
        calendarRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }, [isOpen]);

  return (
    <div ref={containerRef} data-dark-datepicker="true" style={{ position: 'relative', width: '100%' }}>
      {/* Trigger — uses <button> for reliable click handling inside Radix Dialog */}
      <button
        type="button"
        onClick={handleToggle}
        onMouseDown={(e) => { e.stopPropagation(); }}
        style={{
          width: '100%',
          padding: '10px 12px',
          background: 'var(--dp-bg-input, var(--dp-bg-card))',
          border: `1px solid ${isOpen ? 'var(--dp-blue-500, #0DAEFF)' : 'var(--dp-border-subtle)'}`,
          borderRadius: '8px',
          fontSize: '13px',
          color: value ? 'var(--dp-text-primary)' : 'var(--dp-text-tertiary)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'all 0.25s',
          fontFamily: 'var(--dp-font)',
          boxShadow: isOpen ? '0 0 0 3px rgba(13, 174, 255, 0.15)' : 'none',
          outline: 'none',
          boxSizing: 'border-box',
          textAlign: 'left',
        }}
      >
        <span>{value ? formatDisplay(value) : placeholder}</span>
        <Calendar size={16} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0 }} />
      </button>

      {/* Calendar — rendered inline using position:absolute, NOT portal */}
      {isOpen && (
        <div
          ref={calendarRef}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '6px',
            width: '310px',
            background: 'var(--dp-bg-elevated, rgba(30, 30, 36, 0.98))',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(13, 174, 255, 0.15)',
            borderRadius: '14px',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(13, 174, 255, 0.05)',
            zIndex: 99999,
            padding: '16px',
            animation: 'dpCalSlideDown 0.2s ease',
          }}
        >
          {/* Shortcut Buttons */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleShortcut(today); }}
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              style={{
                flex: 1, padding: '7px 0', borderRadius: '8px', border: '1px solid rgba(13, 174, 255, 0.2)',
                background: 'rgba(13, 174, 255, 0.08)', color: 'var(--dp-blue-500, #0DAEFF)',
                fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                fontFamily: 'var(--dp-font)',
              }}
            >
              Hoje
            </button>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleShortcut(tomorrow); }}
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              style={{
                flex: 1, padding: '7px 0', borderRadius: '8px', border: '1px solid var(--dp-border-subtle)',
                background: 'transparent', color: 'var(--dp-text-secondary)',
                fontSize: '12px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
                fontFamily: 'var(--dp-font)',
              }}
            >
              Amanhã
            </button>
            {value && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(''); setIsOpen(false); }}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                style={{
                  padding: '7px 12px', borderRadius: '8px', border: '1px solid rgba(248, 113, 113, 0.2)',
                  background: 'rgba(248, 113, 113, 0.08)', color: '#F87171',
                  fontSize: '12px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
                  fontFamily: 'var(--dp-font)',
                }}
              >
                Limpar
              </button>
            )}
          </div>

          {/* Month/Year Navigation */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '12px',
          }}>
            <button
              type="button"
              onClick={prevMonth}
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              style={{
                width: '32px', height: '32px', borderRadius: '8px', border: 'none',
                background: 'transparent', color: 'var(--dp-text-secondary)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13, 174, 255, 0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <ChevronLeft size={18} />
            </button>
            <span style={{
              fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)',
              fontFamily: 'var(--dp-font)',
            }}>
              {MONTHS_PT[month]} {year}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              style={{
                width: '32px', height: '32px', borderRadius: '8px', border: 'none',
                background: 'transparent', color: 'var(--dp-text-secondary)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13, 174, 255, 0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Day Names Header */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px',
            marginBottom: '6px',
          }}>
            {DAYS_PT.map(day => (
              <div key={day} style={{
                textAlign: 'center', fontSize: '10px', fontWeight: 600,
                color: 'var(--dp-text-tertiary)', padding: '4px 0',
                fontFamily: 'var(--dp-font)', textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                {day}
              </div>
            ))}
          </div>

          {/* Day Grid */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px',
          }}>
            {days.map((day, i) => (
              <div
                key={i}
                role={day ? 'button' : undefined}
                tabIndex={day ? 0 : undefined}
                onClick={(e) => {
                  if (day) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelect(new Date(year, month, day));
                  }
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onKeyDown={(e) => {
                  if (day && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleSelect(new Date(year, month, day));
                  }
                }}
                style={{
                  width: '36px', height: '36px', margin: '0 auto',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '8px', fontSize: '13px', fontWeight: isSelected(day) ? 600 : 400,
                  fontFamily: 'var(--dp-font)',
                  color: !day
                    ? 'transparent'
                    : isSelected(day)
                    ? '#fff'
                    : isToday(day)
                    ? 'var(--dp-blue-500, #0DAEFF)'
                    : 'var(--dp-text-primary)',
                  background: !day
                    ? 'transparent'
                    : isSelected(day)
                    ? 'var(--dp-blue-500, #0DAEFF)'
                    : 'transparent',
                  cursor: day ? 'pointer' : 'default',
                  transition: 'all 0.15s',
                  border: isToday(day) && !isSelected(day) ? '1px solid var(--dp-blue-500, #0DAEFF)' : '1px solid transparent',
                  userSelect: 'none',
                }}
                onMouseEnter={(e) => {
                  if (day && !isSelected(day)) {
                    e.currentTarget.style.background = 'rgba(13, 174, 255, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (day && !isSelected(day)) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                {day || ''}
              </div>
            ))}
          </div>

          <style>{`
            @keyframes dpCalSlideDown {
              from { opacity: 0; transform: translateY(-8px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default DarkDatePicker;
